import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageLayout from '../../PageLayout';
import DesktopProductPage from './desktop';
import MobileProductPage from './mobile';
import API from '@api';
import scrollTo from '@utils/scrollTo';
import { observer } from 'mobx-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  catalogAnalogsQueryOptions,
  productAlsoboughtQueryOptions,
  productInfoTempQueryOptions,
  productListByIdsQueryOptions,
  productRecipesQueryOptions,
  useProductCommentsCountsQuery
} from '@queries/index';
import { useHistory, useParams } from '@compat/router';
import type { CatalogTasksItemsFragmentFragment } from '@schema/cart';
import type {
  BreadCrumbsT,
  ProductPageMethods,
  ProductPageSettings,
  TaskItem
} from './index.type';
import PPLoading from './PPLoading';
import { prepareProductRes } from './prepareProductRes';
import { shownProductPrice } from './shownPrice';
import { pinnedAnalogIds, withPinnedAnalogs } from './pinnedAnalogs';
import FlocktoryProduct from '@components/Flocktory/FlocktoryProduct';
import { IsPrerender } from '@utils/check';
import { productUrl } from '@constants/site';
import JsonLd from '@utils/jsonLd/JsonLd';
import { buildProduct } from '@utils/jsonLd/product';
import usePreviousValue from '@customHooks/usePreviousValue';
import useWindowSize from '@customHooks/useWindowSize';
import type { ScrollBackConfigT } from '@store/UtilsStore';
import { useStores } from '@root/src/stores';
import './component.scss';

export const sortlist = [
  { param: 'dateNew', title: 'Самые свежие' },
  { param: 'dateOld', title: 'Самые давние' },
  { param: 'ratingHigh', title: 'С высоким рейтингом' },
  { param: 'ratingLow', title: 'С низким рейтингом' },
  { param: 'useful', title: 'Самые полезные' },
  { param: 'negative', title: 'Отрицательные' },
  { param: 'positive', title: 'Положительные' }
];

/** Задание по товару из taskItems (вынесено из старого getProductInfo) */
const getTask = (
  tasksItems: CatalogTasksItemsFragmentFragment['taskItems'] | null | undefined
): TaskItem | null => {
  if (tasksItems?.task?.access?.length) {
    return { ...tasksItems.task.access[0], type: 'access' };
  } else if (tasksItems?.task?.progress?.length) {
    return { ...tasksItems.task.progress[0], type: 'progress' };
  }
  return null;
};

const ProductPage: FC = observer(() => {
  const { cartStore, catalogStore, xStore, utilsStore, modalsStore } =
    useStores();
  const { id } = useParams<{
    id: string;
  }>();

  const [setSort, setSetSort] = useState('dateNew');
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const history = useHistory<{
    positionY: number;
    scrollBackConfig: ScrollBackConfigT;
  }>();
  const locationState = history.location.state;
  const pathname = history.location.pathname;
  const isBarMenu = pathname.includes('/bar-menu/');

  // Наблюдаемые чтения в рендере: смена магазина попадает в queryKey и
  // форсирует перезапрос — замена reaction/when из старого getProductInfo
  const { xStoreId } = xStore;
  const { productsAmount, cart, amountStores } = cartStore;
  const amountInCart = productsAmount[id] || 0;
  const { isDesktop } = useWindowSize();
  const {
    isConsentModalOpened,
    isConsentModalAgreed,
    setIsConsentModalOpened
  } = modalsStore;

  // ЭТАП 2 — данные страницы через react-query (был Promise.all из 6 запросов
  // + setState в эффекте: SSR отдавал скелетон PPLoading, а кэш возврата жил
  // в cacheStore — его заменил кэш react-query, gcTime 10 мин). Ключи
  // зеркалятся SSR-префетчем (ssrPrefetch, pristine: xStoreId null);
  // публичный контент приходит в HTML первого ответа, личное (isFavorite,
  // цены под магазин юзера) доезжает после загрузки сторов: xStoreId
  // сменится null -> id и перезапросит.
  // keepPreviousData ОБЯЗАТЕЛЕН: после инициализации сторов xStoreId
  // меняется null -> id магазина, ключ становится новым — без плейсхолдера
  // data на время перезапроса undefined и вся страница мигала PPLoading.
  // Переход товар->товар при этом НЕ показывает старый товар: плейсхолдер
  // держит данные прежнего id, и гейт isCurrentProduct отправляет в PPLoading.
  const { data: productInfoRes } = useQuery({
    ...productInfoTempQueryOptions({
      id: +id,
      isMenu: isBarMenu,
      xStoreId: xStoreId ?? null
    }),
    placeholderData: keepPreviousData
  });
  const productRes = productInfoRes?.getProductByIdTemp;
  // При переходе товар -> товар данных нового id ещё нет — гейт ниже
  // показывает PPLoading (раньше до прихода данных показывался ПРЕЖНИЙ товар)
  const isCurrentProduct = String(productRes?.id ?? '') === String(+id);

  const { data: productRecipesData } = useQuery(
    productRecipesQueryOptions(+id)
  );
  const productRecipes = useMemo(
    () => productRecipesData ?? [],
    [productRecipesData]
  );

  // keepPreviousData — по той же причине (xStoreId в ключе), иначе карусели
  // внизу мигают при смене/инициализации магазина
  const { data: moreProductsData, isSuccess: isMoreProductsLoaded } = useQuery({
    ...productAlsoboughtQueryOptions(+id, xStoreId ?? null),
    placeholderData: keepPreviousData
  });
  const moreProducts = moreProductsData?.getProductAlsoboughtAndRecommend;
  const alsoBought = useMemo(
    () => (moreProducts?.alsobought?.length ? moreProducts.alsobought : []),
    [moreProducts]
  );
  const recommendProducts = useMemo(
    () =>
      moreProducts?.recommendations?.length ? moreProducts.recommendations : [],
    [moreProducts]
  );
  // Старый setIsShowCarouselAlsoBought(true) выставлялся по факту загрузки
  const isShowCarouselAlsoBought = isMoreProductsLoaded;

  const {
    reviewTotalCount,
    commentTotalCount,
    isProductHasAttachmentInComments
  } = useProductCommentsCountsQuery(+id);

  // prepareProductRes — деривация на рендере (данные запроса + сторы);
  // пересчёт при догрузке корзины/магазина происходит сам через observer
  const { productInfo, isProductAvailableForRefund } = useMemo(() => {
    if (!productInfoRes?.getProductByIdTemp) {
      return { productInfo: null, isProductAvailableForRefund: false };
    }
    const activeAmountStores = amountStores.findIndex(
      item => item.id === cart.store_id
    );
    return prepareProductRes({
      res: productInfoRes,
      nearestAvailableIntervalValue:
        amountStores[activeAmountStores]?.nearestAvailableInterval?.value,
      isBarMenu,
      pathname
    });
  }, [productInfoRes, amountStores, cart.store_id, isBarMenu, pathname]);

  const productJsonLd = useMemo(() => {
    if (!productInfoRes?.getProductByIdTemp || !productInfo) {
      return null;
    }
    const shownPrice = shownProductPrice(
      productInfo,
      amountInCart,
      isDesktop ? 'desktop' : 'mobile'
    );
    return buildProduct(
      {
        ...productInfoRes.getProductByIdTemp,
        price: shownPrice.price,
        oldPrice: shownPrice.oldPrice,
        isPreorder: productInfo.isPreorder,
        isProductNotForSale: productInfo.isProductNotForSale
      },
      productUrl(id)
    );
  }, [productInfoRes, productInfo, id, amountInCart, isDesktop]);

  const isFavorite = !!productInfo?.isFavorite;
  const task = useMemo<TaskItem | null>(
    () => getTask(productRes?.taskItems),
    [productRes]
  );

  // Аналоги — только для НЕдоступного товара (клиентская ветка старого
  // getProductInfo; зависит от ответа товара, в SSR-префетч не входит)
  const isProductUnavailable =
    typeof productRes?.available !== 'undefined' && !productRes.available;
  const { data: analogsData } = useQuery({
    ...catalogAnalogsQueryOptions(+id, 10, xStoreId ?? null),
    enabled: isProductUnavailable,
    placeholderData: keepPreviousData
  });
  const pinnedIds = pinnedAnalogIds(id);
  const isAnalogsVertical = pinnedIds.length > 0;
  const { data: pinnedAnalogsData } = useQuery({
    ...productListByIdsQueryOptions(pinnedIds, xStoreId ?? null),
    enabled: isProductUnavailable && isAnalogsVertical
  });
  const analogs = useMemo(
    () =>
      isProductUnavailable
        ? withPinnedAnalogs(
            analogsData ?? [],
            pinnedAnalogsData ?? [],
            pinnedIds
          )
        : [],
    [isProductUnavailable, analogsData, pinnedAnalogsData, pinnedIds]
  );

  const breadCrumbs = useMemo<BreadCrumbsT>(() => {
    if (typeof productRes?.title === 'undefined') {
      return [
        { title: 'Главная страница', url: '/', binding: false },
        { title: 'Каталог', url: '/catalog/', binding: false },
        { title: 'Загрузка', url: '', binding: false }
      ];
    }
    return [
      ...(productRes.bread?.length
        ? productRes.bread.map(item => ({
            title: item.title ?? '',
            url: item.link ?? '',
            binding: !!item.binding
          }))
        : []),
      {
        title: `${productRes.title ?? ''}`,
        url: `/product/${id}`,
        binding: false
      }
    ];
  }, [productRes, id]);

  // Модалка 18+ — клиентский сайд-эффект старого getProductInfo
  useEffect(() => {
    if (
      !!productInfo?.is_18plus &&
      !isConsentModalOpened &&
      !isConsentModalAgreed &&
      !IsPrerender()
    ) {
      setIsConsentModalOpened(true);
    }
  }, [
    productInfo?.is_18plus,
    isConsentModalOpened,
    isConsentModalAgreed,
    setIsConsentModalOpened
  ]);

  const getHashTab = useCallback((hash: string | undefined): number => {
    let index = 0;
    if (hash === '#/') {
      index = 0;
    }
    if (hash === '#/tab/reviews') {
      index = 1;
    }
    if (hash === '#/tab/questions') {
      index = 2;
    }
    if (hash === '#/tab/recipes') {
      index = 3;
    }
    if (hash === '#/tab/videos') {
      index = 4;
    }
    return index;
  }, []);

  /**
   * СТАРТОВАЯ ВКЛАДКА — ВСЕГДА ПЕРВАЯ, ХЕШ ПРИМЕНЯЕМ ПОСЛЕ ГИДРАЦИИ
   * (багфикс 30.08.2026). Раньше начальное состояние считалось прямо из
   * `location.hash`, а фрагмент адреса НИКОГДА не доходит до сервера: по
   * HTTP он не отправляется в принципе. Значит сервер рисовал первую
   * вкладку, а первый клиентский рендер по `/product/15#/tab/reviews` —
   * вкладку отзывов. React видел другой текст (ошибка #418) и перерисовывал
   * дерево целиком.
   *
   * Ссылка на вкладку при этом продолжает работать: переключаемся сразу
   * после монтирования, то есть в том же кадре, что и гидрация.
   */
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const tabFromHash = getHashTab(history.location.hash || '');
    if (tabFromHash !== 0) {
      setActiveTab(tabFromHash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollTo.params({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (locationState) {
      if (locationState.positionY) {
        catalogStore.setPositionY(locationState.positionY);
      }
      if (locationState.scrollBackConfig) {
        utilsStore.setScrollBack(locationState.scrollBackConfig);
      }
    }
  }, [locationState, catalogStore, utilsStore]);

  const setHashTab = useCallback(
    (activeTab: number): void => {
      let hash = '';
      if (activeTab === 1) {
        hash = '#/tab/reviews';
      }
      if (activeTab === 2) {
        hash = '#/tab/questions';
      }
      if (activeTab === 3) {
        hash = '#/tab/recipes';
      }
      if (activeTab === 4) {
        hash = '#/tab/videos';
      }
      try {
        history.replace({ ...history.location, hash });
      } catch (e) {
        console.log(e);
      }
    },
    [history]
  );

  const prevId = usePreviousValue(id);
  const prevActiveTab = usePreviousValue(activeTab);

  useEffect(() => {
    if (id !== prevId) {
      scrollTo.params({ top: 0, behavior: 'smooth' });
      setActiveTab(0);
    }
    if (prevActiveTab !== null && prevActiveTab !== activeTab) {
      setHashTab(activeTab);
    }
  }, [activeTab, id, prevActiveTab, prevId, setHashTab]);

  const _commentsResolver: ProductPageMethods['_commentsResolver'] = async (
    page,
    setLimit,
    commentType,
    withFile
  ) =>
    await API.getCommentsProduct({
      catalogId: +id,
      page,
      setLimit,
      setSort,
      commentType,
      withFile: !!withFile
    }).then(res => res.getProductCommentsById);

  const changeSort: ProductPageMethods['changeSort'] = param => {
    setSetSort(param);
  };

  const changeUrlToUpBreadCrumbs = (): void => {
    let url;
    if (breadCrumbs.length > 2) {
      url = breadCrumbs[breadCrumbs.length - 2].url;
    } else {
      url = '/catalog';
    }

    history.push(url);
  };

  const scrollToReviews = (): void => {
    setActiveTab(1);
    if (tabsRef?.current) {
      const yOffset = window.innerWidth >= 768 ? -140 : -180;
      const y =
        tabsRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const linkToQuestions = (): void => {
    setActiveTab(2);
  };

  const breadCrumbsLastItem = useMemo(() => {
    let title: string;

    if (breadCrumbs.length > 2) {
      title = breadCrumbs[breadCrumbs.length - 2].title;
    } else {
      title = 'Каталог';
    }
    return `Все ${title?.toLowerCase()}`;
  }, [breadCrumbs]);

  const isItemInCart = productsAmount[id] > 0;

  // Гейт: нет данных ИЛИ в кэше ещё предыдущий товар (переход по SPA)
  if (!productInfo || !isCurrentProduct) {
    return <PPLoading />;
  }

  // Детерминированный выбор рецепта товара (был getRandomNumber): SSR и
  // гидрация обязаны показать ОДИН и тот же рецепт — random дал бы mismatch
  const productRecipe =
    productRecipes[productRecipes.length ? +id % productRecipes.length : 0];

  const settings: ProductPageSettings = {
    id,
    isConsentModalAgreed,
    canBeBought: productInfo.canBeBought,
    isFavorite,
    productInfo,
    breadCrumbsLastItem,
    isItemInCart,
    amountInCart,
    breadCrumbs,
    productRecipe,
    alsoBought,
    analogs,
    isAnalogsVertical,
    recommendProducts,
    isShowCarouselAlsoBought,
    reviewTotalCount,
    activeTab,
    setSort,
    isProductAvailableForRefund,
    commentTotalCount,
    isProductHasAttachmentInComments,
    productRecipes,
    ref: tabsRef,
    task,
    changeSort,
    _commentsResolver,
    linkToQuestions,
    setActiveTab,
    changeUrlToUpBreadCrumbs,
    scrollToReviews
  };

  return (
    <>
      <JsonLd data={productJsonLd} />
      <FlocktoryProduct productId={productInfo.id} />
      <PageLayout
        renderDesktop={DesktopProductPage}
        settingDesktop={settings}
        renderLaptop={MobileProductPage}
        settingLaptop={settings}
        renderMobile={MobileProductPage}
        settingMobile={settings}
      />
    </>
  );
});

export default ProductPage;
